<!-- Copyright (c) 2026 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div class="batch-posting-main">
    <div class="left-menu">
      <Menus
        :menu="menu"
        @menuClicked="menuClicked"
        style="height: auto; border-bottom-left-radius: 0; border-bottom-right-radius: 0"
        :collapsible="false"
        :menuWidth="250"
      />
      <div class="menu-bottom">
        <BoxContainer title="Filter">
          <DateTimePicker
            :showTime="false"
            :showMonthOnly="true"
            placeholder="Process Pay for"
            @update:value="dateChanged"
            :value.sync="selectedDate"
            :isRequired="true"
          />
          <button @click="refreshClicked" :disabled="!selectedDate"> Refresh </button>
        </BoxContainer>
        <div class="batch-history-container">
          <div v-if="lastRefreshed" style="margin-bottom: 6px">Last Refreshed: {{ lastRefreshed }}</div>
          <div v-if="payrollHistory && payrollHistory.length > 0" class="batch-history">
            <div
              v-for="(p, i) in payrollHistory"
              :key="i"
              class="batch-history-card"
              :style="p == selectedPayrollBatch ? 'background-color: #7878ff; color: white;' : ''"
              @click="loadProcessedPayrollList(p)"
            >
              <div style="display: flex; justify-content: space-between">
                <span>Date: {{ p['Process Date'] }}</span>
                <span style="font-weight: bold">#{{ p['#'] }}</span>
              </div>
              <div>Payrolls: {{ p['No. of Payrolls'] }}</div>
              <div>Total Payout: {{ p['Total Payout'] }}</div>
            </div>
          </div>
          <div v-else class="batch-history-empty">
            {{ payrollHistoryLoading ? 'Loading...' : 'No items' }}
          </div>
        </div>
      </div>
    </div>
    <div class="right-container">
      <div class="status-section">
        <div class="status-buttons">
          <button
            :class="{ 'btn-paid': !hidePaidPayrolls, 'btn-paid-hide': hidePaidPayrolls }"
            @click="payrollFilterButtonClicked('P')"
          >
            <span v-if="hidePaidPayrolls"><s>Paid</s></span>
            <span v-else>Paid</span>
          </button>
          <button
            :class="{ 'btn-pending': !hideNewPayrolls, 'btn-pending-hide': hideNewPayrolls }"
            @click="payrollFilterButtonClicked('N')"
          >
            <span v-if="hideNewPayrolls"><s>New</s></span>
            <span v-else>New</span>
          </button>
          <button
            :class="{ 'btn-reversed': !hideReversedPayrolls, 'btn-reversed-hide': hideReversedPayrolls }"
            @click="payrollFilterButtonClicked('C')"
          >
            <span v-if="hideReversedPayrolls"><s>Reversed</s></span>
            <span v-else>Reversed</span>
          </button>
        </div>
        <div v-if="showTypeWarning" class="legend-warning">
          <img src="https://dsmlib.logistics.systems/Icons/warning.png" alt="Missing payroll type" />
          <span>No payroll type to calculate Basic Pay & Bonus Incentive</span>
        </div>
      </div>
      <div class="payrolls-generated">
        <TableBox
          :title="payrollListTitle"
          :tableData="payrollTableDisplayData"
          :dataType="payrollTableDataType"
          :selectedRow.sync="selectedPayrolls"
          :maxSelection="-1"
          :hideColumns="['Remarks', 'OverTime', 'PayMonth', 'PayYear']"
          :showSelectAll="true"
          :loading="payrollListLoading"
        />
      </div>
    </div>
    <div v-if="showProcessPayPopup" class="process-pay-overlay">
      <div class="process-pay-modal">
        <div class="process-pay-header">
          Process Monthly Payment ({{ processPayrollStartDate.formatString('dd/MM/yyyy') }} -
          {{ processPayrollEndDate.formatString('dd/MM/yyyy') }})
        </div>
        <div class="process-pay-body">
          <div class="process-pay-top">
            <InputQuery
              placeholder="Department"
              :items="deptList"
              itemKey="Code - Desc"
              :selectedObject.sync="selectedDept"
              :isDisabled="true"
              :tableBoxHideColumns="['Code - Desc']"
              style="width: auto; flex-grow: 1"
            />
            <CheckBoxV2
              title="Use approved driver commissions"
              :isChecked.sync="useApprovedRates"
              style="align-content: center; padding-bottom: 5px"
            />
          </div>
          <TableBox
            style="height: 90%"
            title="Employees with Outstanding Payment"
            :tableData="driverList"
            :selectedRow.sync="selectedDrivers"
            :maxSelection="-1"
            :showSelectAll="true"
            :hideColumns="['Start Day', 'End Day']"
          />
        </div>
        <div class="process-pay-footer">
          <button class="process-pay-btn" @click="processPayPopupClicked('Process Pay Now')"> Process Pay Now </button>
          <button class="process-pay-btn" @click="processPayPopupClicked('Cancel')" style="background-color: lightgray">
            Cancel
          </button>
        </div>
      </div>
    </div>
    <div v-if="showUnpaidLeavePopup" class="process-pay-overlay">
      <div class="process-pay-modal">
        <div class="process-pay-header">
          Generate Unpaid Leave from Clock In/Out Attendance ({{ unpaidLeaveStartDate }} - {{ unpaidLeaveEndDate }})
        </div>
        <div class="process-pay-body">
          <TableBox
            style="height: 90%"
            title="Employees with Unpaid Leave"
            :tableData="unpaidLeaveList"
            :selectedRow.sync="selectedDriversUnpaidLeave"
            :maxSelection="-1"
            :showSelectAll="true"
            :hideColumns="['AT1', 'AT2']"
          />
        </div>
        <div class="process-pay-footer">
          <button class="process-pay-btn" @click="unpaidLeavePopupClicked('Generate')"> Generate </button>
          <button
            class="process-pay-btn"
            @click="unpaidLeavePopupClicked('Reverse')"
            style="background-color: rgb(255, 179, 179)"
          >
            Reverse
          </button>
          <button
            class="process-pay-btn"
            @click="unpaidLeavePopupClicked('Cancel')"
            style="background-color: lightgray"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    payrollStatusToHide() {
      return [
        this.hidePaidPayrolls ? 'P' : null,
        this.hideNewPayrolls ? 'N' : null,
        this.hideReversedPayrolls ? 'C' : null,
      ].filter(Boolean);
    },
    payrollTableDisplayData() {
      // create status chips
      const tones = { N: 'warn', P: 'ok', C: 'bad' };
      const keys = { N: 'New', P: 'Paid', C: 'Reversed' };
      return this.payrollListDisplay.map((item) => {
        if (!Object.prototype.hasOwnProperty.call(item, 'Indicator')) return { ...item };
        const status = String(item['Indicator'] ?? '').trim();
        const tone = tones[status] || 'neutral';
        return {
          ...item,
          Indicator: status ? `<span class="status-chip status-chip--${tone}">${keys[status]}</span>` : '',
        };
      });
    },
  },
  data: function () {
    return {
      menu: [
        {
          title: 'Process Pay',
          icon: 'https://dsmlib.logistics.systems/Icons/money.png',
          enabled: false,
          visible: true,
          key: 'MnuProcessPay',
        },
        {
          title: 'Reverse Payroll(s)',
          icon: 'https://dsmlib.logistics.systems/Icons/Document_Cancel.png',
          enabled: false,
          visible: true,
          key: 'MnuReversePayroll',
        },
        {
          title: 'Generate Unpaid Leave',
          icon: 'https://dsmlib.logistics.systems/Icons/History_record.svg',
          enabled: false,
          visible: true,
          key: 'MnuUnpaidLeave',
        },
        {
          title: 'Print Payslip/Settlement',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: false,
          visible: true,
          key: 'MnuPrintPayslip',
        },
        {
          title: 'Print Summary of Payout',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: false,
          visible: true,
          key: 'MnuPrintPaySummary',
        },
        {
          title: 'Print EA Form',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: false,
          visible: true,
          key: 'MnuPrintEAForm',
        },
      ],
      selectedDate: null,
      selectedMonth: '',
      selectedYear: '',
      useApprovedRates: false,
      lastRefreshed: '',
      payrollHistory: [],
      selectedPayrollBatch: [],
      payrollList: [],
      payrollListDisplay: [],
      selectedPayrolls: [],
      payrollHistoryTitle: '',
      payrollListTitle: '',
      payrollListLoading: false,
      payrollHistoryLoading: false,
      payrollTableDataType: {
        'Nett Pay': 'number',
        Basic: 'number',
        Revenue: 'number',
        Commission: 'number',
        'Special Day': 'number',
        'Extra Inc.': 'number',
        Expenses: 'number',
        'Loan Deduct': 'number',
        Violations: 'number',
        Bonus: 'number',
        SOCSO: 'number',
        EPF: 'number',
        'Employer SOCSO': 'number',
        'Employer EPF': 'number',
        Tabung: 'number',
        'Income Tax': 'number',
        'Unpaid Leave': 'number',
        'Annual Bonus': 'number',
        SIP: 'number',
        'Employer SIP': 'number',
      },

      showProcessPayPopup: false,
      driverList: [],
      selectedDrivers: [],
      deptList: [],
      selectedDept: null,
      processPayrollStartDate: null,
      processPayrollEndDate: null,

      showUnpaidLeavePopup: false,
      unpaidLeaveList: [],
      selectedDriversUnpaidLeave: [],
      unpaidLeaveStartDate: null,
      unpaidLeaveEndDate: null,

      hidePaidPayrolls: false,
      hideNewPayrolls: false,
      hideReversedPayrolls: false,
      showTypeWarning: false,
    };
  },
  methods: {
    async sasControlFunction() {
      this.$fullscreenLoading('Checking Security Access');
      const sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=HMS&page=Driver Batch Payroll`);
      this.menu.forEach((m) => {
        const sas = sasControl.find((k) => k['Control'] == m['key']);
        if (sas) m[sas['Properties'].toLowerCase()] = sas['PropertiesValue'] == 'True';
        else m['enabled'] = true;
      });
      this.$fullscreenLoading('');
    },
    menuClicked: async function (menu) {
      switch (menu['title']) {
        case 'Process Pay':
          await this.loadDriversProcessPay();
          this.showProcessPayPopup = true;
          break;
        case 'Reverse Payroll(s)':
          if (this.selectedPayrolls.length == 0) {
            this.$showAlert('error', 'No Payroll Selected', 'Please select one or more payrolls to reverse');
            return;
          }
          await this.reverseSelectedPayrolls(this.selectedPayrolls.length);
          break;
        case 'Generate Unpaid Leave':
          await this.loadUnpaidLeaveList();
          this.showUnpaidLeavePopup = true;
          break;
        case 'Print Payslip/Settlement':
          if (this.selectedPayrollBatch.length == 0) {
            this.$showAlert('error', 'No Batch Selected', 'Please select a payroll batch to proceed');
            return;
          }
          this.openPrintWindow('Print Payslip/Settlement');
          break;
        case 'Print Summary of Payout':
          if (this.selectedPayrollBatch.length == 0) {
            this.$showAlert('error', 'No Batch Selected', 'Please select a payroll batch to proceed');
            return;
          }
          this.openPrintWindow('Print Summary of Payout');
          break;
        case 'Print EA Form':
          this.openPrintWindow('Print EA Form');
          break;
      }
    },
    refreshClicked: async function () {
      this.payrollHistory = [];
      this.payrollList = [];
      this.payrollListDisplay = [];
      this.selectedPayrollBatch = [];
      this.payrollHistoryLoading = true;

      const dataToPost = {
        system: 'HMS',
        month: this.selectedMonth,
        year: this.selectedYear,
      };

      this.payrollHistory = await this.$apiCallV2('/DriverPayroll/GetBatchPayrollHistory', dataToPost);
      this.payrollHistoryTitle = `History of Batch Payroll Processes for ${this.selectedMonth}/${this.selectedYear}`;

      // combine date and time columns into one
      this.payrollHistory.forEach((p) => {
        const date = new Date(p['Process Date']);
        const formattedTime = p['Process Time'].slice(0, 2) + ':' + p['Process Time'].slice(2);
        p['Process Date'] = date.formatString('yyyy-MM-dd') + ' ' + formattedTime;
      });
      this.payrollHistoryLoading = false;
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm:ss');
    },
    loadProcessedPayrollList: async function (batch) {
      this.payrollList = [];
      this.payrollListDisplay = [];
      this.selectedPayrolls = [];
      this.selectedPayrollBatch = batch;

      const dataToPost = {
        monthprocessid: batch['MonthProcessID'],
        getallbatches: '0',
      };
      this.payrollListTitle = `Payrolls Generated for Batch #${batch['#']} of ${this.selectedMonth}/${this.selectedYear}`;
      this.payrollListLoading = true;
      this.payrollList = await this.$apiCallV2('/DriverPayroll/GetBatchPayrollListing', dataToPost);
      this.payrollListDisplay = this.payrollList;

      this.showTypeWarning = false;
      for (const row of this.payrollList) {
        // add warning symbol for drivers with no payroll type
        if (row['Type'] == '') {
          row['Type'] =
            `<img style="height: 20px; width: 20px;" src="https://dsmlib.logistics.systems/Icons/warning.png">`;

          this.showTypeWarning = true;
        }
      }

      this.payrollListLoading = false;
    },
    dateChanged: function (value) {
      if (!value) {
        this.selectedMonth = '';
        this.selectedYear = '';
        return;
      }
      this.selectedMonth = value.formatString('MM');
      this.selectedYear = value.formatString('yyyy');
    },
    reverseSelectedPayrolls: async function (count) {
      const reversedPayrolls = this.selectedPayrolls.filter((p) => p['Indicator'] == 'C');

      // check for payrolls that were already reverted
      if (reversedPayrolls.length > 0) {
        this.$showAlert('error', 'Unable to Reverse', 'Please select payrolls which have not already been reversed.');
        return;
      }

      const reverse = await this.$showAlert(
        'warning',
        `Reverse Payrolls`,
        `Are you sure you want to reverse the selected ${count} payroll${count == 1 ? '' : 's'}?`,
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (reverse == 'Yes') {
        this.$fullscreenLoading('Reversing payrolls');
        const dataToPost = {
          system: 'HMS',
          payrolls: [],
        };

        // able to reverse multiple payrolls at once
        this.selectedPayrolls.forEach((p) => {
          const payrollData = {
            payrollno: p['Payroll #'],
            type: p['Indicator'],
            paymentno: p['Payment #'],
          };
          dataToPost['payrolls'].push(payrollData);
        });

        const result = await this.$apiCallV2('/DriverPayroll/ReversePayroll', dataToPost);

        if (result[0]?.['Results'] == '1') {
          this.$showAlert('error', 'Unable to Reverse', result[0]['ErrMsg']);
        } else {
          this.$showAlert('success', 'Payrolls Reversed Successfully');
          // reload the same view as before to show changes
          await this.loadProcessedPayrollList(this.selectedPayrollBatch);
        }

        this.$fullscreenLoading();
      }
    },
    processPayPopupClicked: async function (btn) {
      if (btn == 'Cancel') {
        this.showProcessPayPopup = false;
      } else if (btn == 'Process Pay Now') {
        if (this.selectedDrivers.length == 0) {
          this.$showAlert('error', 'No Employee Selected', 'Please select one or more employees');
          return;
        }
        await this.processPayrollBatch();
      }
      this.selectedDrivers = [];
    },
    loadDriversProcessPay: async function () {
      this.$fullscreenLoading('Getting Employees');
      const dataToPost = {
        type: '0',
        year: this.selectedYear,
        month: this.selectedMonth,
        system: 'HMS',
      };

      const result = await this.$apiCallV2('/DriverPayroll/GetPayrollEmployeesList', dataToPost);
      this.driverList = result['employees'];
      this.processPayrollStartDate = new Date(result['startDate']);
      this.processPayrollEndDate = new Date(result['endDate']);

      this.selectedDept = this.deptList.getSelected(this.driverList[0]['Department'], 'DeptDesc');
      this.$fullscreenLoading();
    },
    processPayrollBatch: async function () {
      // generate unique process ID for this batch
      const processID = crypto.randomUUID();
      let seq = 1;
      this.$fullscreenLoading(`Processing Pay (0/${this.selectedDrivers.length})`);

      for (const d of this.selectedDrivers) {
        // get trip commission for driver
        const drvIncData = {
          driverselection: d['Staff No'],
          jiselection: d['Staff No'],
          attendantjiselection: d['Staff No'],
          showallentry: '0',
          startdate: this.processPayrollStartDate.formatString('yyyy-MM-dd'),
          enddate: this.processPayrollEndDate.formatString('yyyy-MM-dd'),
          getactcompletedate: '0',
          method: '0',
        };
        const tripCommission = await this.$apiCallV2('/MovementUpdate/GetDriverIncentive', drvIncData);

        if (tripCommission) {
          // create new payroll
          const dataToPost = {
            processid: processID,
            system: 'HMS',
            startdate: this.processPayrollStartDate.formatString('yyyy-MM-dd'),
            enddate: this.processPayrollEndDate.formatString('yyyy-MM-dd'),
            month: this.selectedMonth,
            year: this.selectedYear,
            staffno: d['Staff No'],
            comtype: d['Type'],
            tripcommission: tripCommission[0],
            useapprovedrates: this.useApprovedRates ? '1' : '0',
          };

          await this.$apiCallV2('/DriverPayroll/ProcessPayroll', dataToPost);
          this.$fullscreenLoading(`Processing Pay (${seq++}/${this.selectedDrivers.length})`);
        }
      }

      this.$fullscreenLoading();
      this.$showAlert('success', 'Payrolls Processed Successfully');
      this.showProcessPayPopup = false;
      await this.refreshClicked();
    },
    loadUnpaidLeaveList: async function () {
      this.$fullscreenLoading('Getting Employees');
      this.unpaidLeaveList = [];
      const dataToPost = {
        type: '1',
        year: this.selectedYear,
        month: this.selectedMonth,
        system: 'HMS',
      };

      const result = await this.$apiCallV2('/DriverPayroll/GetPayrollEmployeesList', dataToPost);
      this.unpaidLeaveStartDate = new Date(result['startDate']).formatString('dd/MM/yyyy');
      this.unpaidLeaveEndDate = new Date(result['endDate']).formatString('dd/MM/yyyy');

      let seq = 1;
      result['employees'].forEach((e) => {
        this.unpaidLeaveList.push({
          '#': seq,
          'Staff No': e['Staff_No'],
          'Staff Name': e['Staff_Name'],
          'Basic Pay': e['Basic_Pay1'],
          'Total AT': e['TotalAT'],
          'Deduct Amount': e['UnpaidLeaveToDeduct'],
          'Unpaid Leave': e['UnpaidLeave'],
          'Payroll No': e['PayrollNo'],
        });
        seq++;
      });
      this.$fullscreenLoading();
    },
    unpaidLeavePopupClicked: async function (btn) {
      switch (btn) {
        case 'Cancel':
          this.showUnpaidLeavePopup = false;
          break;
        case 'Generate':
          if (this.selectedDriversUnpaidLeave.length == 0) {
            this.$showAlert('error', 'No Employee Selected', 'Please select an employee to generate unpaid leave.');
            return;
          }
          await this.generateUnpaidLeave('N');
          break;
        case 'Reverse':
          if (this.selectedDriversUnpaidLeave.length == 0) {
            this.$showAlert('error', 'No Employee Selected', 'Please select an employee to reverse unpaid leave.');
            return;
          }
          await this.generateUnpaidLeave('D');
          break;
      }
      this.selectedDriversUnpaidLeave = [];
    },
    generateUnpaidLeave: async function (ind) {
      // check if payroll has been created
      const alreadyPaid = this.selectedDriversUnpaidLeave.filter(
        (d) => d['Payroll No'] != '' && d['Payroll No'] != null,
      );
      if (alreadyPaid.length > 0) {
        this.$showAlert('error', 'Payroll Exists', 'Payroll has already been created for one or more employees.');
        return;
      }

      // check if leave was already generated
      const alreadyGenerated = this.selectedDriversUnpaidLeave.filter(
        (d) => d['Unpaid Leave'] != '' && d['Unpaid Leave'] != null,
      );
      if (ind == 'N' && alreadyGenerated.length > 0) {
        this.$showAlert(
          'error',
          'Unable to Generate',
          'Unpaid leave has already been generated for one or more employees.',
        );
        return;
      } else if (ind == 'D' && alreadyGenerated.length == 0) {
        this.$showAlert(
          'error',
          'Unable to Generate',
          'Unpaid leave has not yet been generated for one or more employees.',
        );
        return;
      }

      this.$fullscreenLoading('Generating Unpaid Leave');
      const dataToPost = {
        action: ind,
        startdate: this.unpaidLeaveStartDate,
        enddate: this.unpaidLeaveEndDate,
        month: new Date(this.selectedYear, this.selectedMonth - 1, 1).formatString('yyyy-MM-dd'),
        employees: [],
      };

      this.selectedDriversUnpaidLeave.forEach((e) => {
        const employeeData = {
          staffno: e['Staff No'],
          basicpay: e['Basic Pay'],
          deductamount: e['Deduct Amount'],
          unpaidleave: e['Unpaid Leave'],
          payrollno: e['Payroll No'],
        };
        dataToPost['employees'].push(employeeData);
      });

      const result = await this.$apiCallV2('/DriverPayroll/SaveEmployeeUnpaidLeave', dataToPost);

      if (result[0]?.['Results'] == '0') {
        if (ind == 'N') this.$showAlert('success', 'Unpaid Leave Generated Successfully.');
        else this.$showAlert('success', 'Unpaid Leave Reversed Successfully.');
        // reload table view
        await this.loadUnpaidLeaveList();
      }
      this.$fullscreenLoading();
    },
    openPrintWindow: function (btn) {
      // open a new tab to print the selected doc type
      let path = '';
      switch (btn) {
        case 'Print Payslip/Settlement':
          path = this.$router.resolve({
            path: `/Home/${this.$route.params.user}/Haulage/Print Driver Settlement`,
            query: {
              processid: this.selectedPayrollBatch['MonthProcessID'],
              date: new Date(this.selectedPayrollBatch['Process Date']).formatString('yyyy-MM-dd'),
            },
          });
          break;
        case 'Print Summary of Payout':
          path = this.$router.resolve({
            path: `/Home/${this.$route.params.user}/Haulage/Print Payout Summary`,
            query: {
              date: new Date(this.selectedPayrollBatch['Process Date']).formatString('yyyy-MM-dd'),
            },
          });
          break;
        case 'Print EA Form':
          path = this.$router.resolve({
            path: `/Home/${this.$route.params.user}/Print EA Form`,
            query: {
              year: this.selectedYear,
            },
          });
          break;
      }

      window.open(path.href, '_blank');
    },
    payrollFilterButtonClicked: function (status) {
      switch (status) {
        case 'C':
          this.hideReversedPayrolls = !this.hideReversedPayrolls;
          break;
        case 'P':
          this.hidePaidPayrolls = !this.hidePaidPayrolls;
          break;
        case 'N':
          this.hideNewPayrolls = !this.hideNewPayrolls;
          break;
      }

      // update table view based on filtered status
      this.payrollListDisplay = [];
      this.$nextTick(() => {
        this.payrollListDisplay = this.payrollList.filter((p) => {
          return !this.payrollStatusToHide.includes(p['Indicator']);
        });
      });
    },
  },
  async mounted() {
    this.payrollListTitle = 'Payrolls Generated';
    this.selectedDate = new Date();
    this.dateChanged(this.selectedDate);

    await this.sasControlFunction();
    this.$fullscreenLoading('Getting Details');
    await this.refreshClicked();

    await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'DEPARTMENT' }).then((result) => {
      this.deptList = result.map((dept) => ({
        ...dept,
        'Code - Desc': `${dept['ID']} - ${dept['DeptDesc']}`,
      }));
    });
    this.$fullscreenLoading();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.batch-posting-main {
  font-size: 12px;
  padding: 10px 45px;
  width: 100%;
  height: 100%;
  display: flex;

  > .left-menu {
    display: flex;
    flex-direction: column;

    > .menu-bottom {
      background-color: white;
      border-bottom-right-radius: 5px;
      border-bottom-left-radius: 5px;
      display: flex;
      flex-direction: column;
      padding: 0 5px 5px 5px;
      flex-grow: 1;
      max-width: 250px;
      min-height: 0;
      gap: 6px;

      > .batch-history-container {
        background-color: #d3d3d3;
        padding: 5px;
        width: 100%;
        height: 100%;
        overflow: auto;

        > .batch-history {
          display: flex;
          flex-direction: column;
          gap: 5px;

          > .batch-history-card {
            background-color: white;
            border-radius: 5px;
            padding: 5px;
            cursor: pointer;

            &:hover {
              background-color: #b3c4ff;
            }
          }
        }

        > .batch-history-empty {
          display: flex;
          justify-content: center;
        }
      }
    }
  }

  > .right-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    margin-left: 5px;
    overflow: hidden;
    gap: 5px;

    > * {
      background-color: white;
      padding: 5px;
      border-radius: 5px;
      display: flex;
    }

    > .status-section {
      display: flex;
      justify-content: space-between;

      > * {
        margin-left: 5px;
      }

      > .status-buttons {
        flex-direction: row;
        display: flex;
        gap: 6px;

        > * {
          transition: none;
          box-shadow: none;
        }

        > .btn-paid {
          background-color: #2e7d32;
          color: #ffffff;
          border: 1px solid #2e7d32;
        }

        > .btn-pending {
          background-color: #ffa500;
          color: #ffffff;
          border: 1px solid #ffa500;
        }

        > .btn-reversed {
          background-color: #c62828;
          color: #ffffff;
          border: 1px solid #c62828;
        }

        > .btn-paid-hide {
          background-color: #ffffff;
          color: #2e7d32;
          border: 1px solid;
        }

        > .btn-pending-hide {
          background-color: #ffffff;
          color: #ffa500;
          border: 1px solid;
        }

        > .btn-reversed-hide {
          background-color: #ffffff;
          color: #c62828;
          border: 1px solid;
        }
      }
    }

    > .payroll-history {
      overflow: auto;
      padding-bottom: 10px;
    }

    > .payrolls-generated {
      overflow: auto;
      height: 100%;
    }
  }
}

.menu-action {
  margin-bottom: 5px;
  display: flex;
  gap: 5px;
  text-align: left;
  width: 100%;
  padding: 5px;

  > .menu-action-icon {
    height: 20px;
    width: 20px;
  }
}

.process-pay-overlay {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.3);
  top: 50px;
  left: 0;
  height: calc(100% - 50px);
  width: 100%;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  > .process-pay-modal {
    background-color: white;
    width: 60%;
    height: 95%;
    border-radius: 3px;
    position: relative;
    display: flex;
    overflow: hidden;
    flex-direction: column;

    > .process-pay-header {
      padding: 12px 12px 0;
      text-align: center;
      margin: 0;
      font-size: 1.3em;
      font-weight: bold;
    }

    > .process-pay-body {
      padding: 12px 12px 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 6px;
      flex: 1;
      min-height: 0;

      > .process-pay-top {
        display: flex;
        gap: 12px;
      }
    }

    > .process-pay-footer {
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      padding-bottom: 6px;
      background: #fff;

      > .process-pay-btn {
        min-width: 80%;
        max-width: 80%;
        margin: 3px 0;
      }
    }
  }
}
.legend-warning {
  display: flex;
  align-items: center;
  gap: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  img {
    width: 20px;
    height: 20px;
  }

  span {
    line-height: 1;
  }
}
</style>
